import { useCallback, useEffect, useRef, useState } from 'react';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    useListNavigation,
    FloatingFocusManager,
    offset,
    flip,
    size,
    autoUpdate,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import Spinner from 'Components/Atoms/Spinner';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import selectVariants from 'Components/Atoms/Select/SelectVariants';
import { cn } from 'Utils/cn';
import MultiselectItem from 'Components/Molecules/Multiselect/MultiselectItem';
import MultiselectFilter from 'Components/Molecules/Multiselect/MultiselectFilter';
import ButtonClear from 'Components/Atoms/ButtonClear/ButtonClear';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineArrowBottom from 'Components/Atoms/OutlineIcons/OutlineArrowBottom';
import FloatLabel from 'Components/Atoms/FloatLabel/FloatLabel';

const MultiselectInfinite = (props) => {
    const {
        label,
        size: selectSize,
        options,
        optionsSelected,
        onChangeSelected,
        className,
        disabled,
        query,
        onChangeQuery,
        isFetching,
        fetchNextPage,
        hasNextPage,
        emptyValue,
        floatLabel,
        enableClear = false,
        onClear,
    } = props;
    const sentinelRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);
    const listRef = useRef([]);

    const handleFilter = (ev) => {
        onChangeQuery(ev.target.value);
        setActiveIndex(null);
    };

    const optionsFiltered = options.filter((option) => option.id !== emptyValue);
    const emptyOption = emptyValue ? options.find((option) => option.id === emptyValue) : null;

    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open: isOpen,
        onOpenChange: (open) => {
            setIsOpen(open);
            if (!open) {
                onChangeQuery('');
                setActiveIndex(null);
            }
        },
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(4),
            flip(),
            size({
                apply({ rects, elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        minWidth: `${rects.reference.width}px`,
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    useEffect(() => {
        if (sentinelRef.current) {
            const observer = new window.IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                });
            });
            observer.observe(sentinelRef.current);
            return () => observer.disconnect();
        }
    }, [sentinelRef, hasNextPage, isFetching, isOpen]);

    const listNav = useListNavigation(context, {
        listRef,
        activeIndex,
        onNavigate: setActiveIndex,
        virtual: true,
        loop: true,
        focusItemOnOpen: true,
    });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
        listNav,
    ]);

    const handleFloatingKeyDown = useCallback(
        (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && activeIndex != null && optionsFiltered[activeIndex]) {
                e.preventDefault();
                handleToggle(optionsFiltered[activeIndex].id);
            }
        },
        [activeIndex, optionsFiltered],
    );

    const handleToggle = (optionId) => {
        if (!Array.isArray(optionsSelected)) {
            onChangeSelected([optionId]);
            return;
        }
        if (optionsSelected.includes(optionId)) {
            const newSelected = optionsSelected.filter((o) => o !== optionId);
            if (newSelected.length === 0) {
                onChangeSelected(null);
            } else {
                onChangeSelected(newSelected);
            }
        } else {
            onChangeSelected([...optionsSelected, optionId]);
        }
    };

    const isChecked = (optionId) => {
        if (!Array.isArray(optionsSelected)) return false;
        return optionsSelected.includes(optionId);
    };

    return (
        <div className="relative min-w-0 group">
            <FloatLabel label={floatLabel} isOpen={isOpen} />

            <button
                ref={refs.setReference}
                type="button"
                disabled={disabled}
                className={cn(
                    selectVariants({ size: selectSize, className }),
                    'gap-1',
                    isOpen && 'border-primary ring-2 ring-primary/20',
                    optionsSelected.length === 0 && 'text-dark-blue/50',
                )}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                {...getReferenceProps()}
            >
                <div className="truncate grow min-w-0">{label}</div>
                {enableClear && !disabled && <ButtonClear onClear={onClear} />}
                <OutlineChevronDown size={20} className="shrink-0 text-dark-blue" color="currentColor" />
            </button>
            <AnimatePresence>
                {isOpen && (
                    <FloatingFocusManager context={context}>
                        <motion.div
                            ref={refs.setFloating}
                            style={{ ...floatingStyles, position: strategy, top: y ?? 0, left: x ?? 0 }}
                            className="mt-1 w-full overflow-x-hidden overflow-y-auto scrollbar-thin rounded-md bg-white shadow-sm ring-1 ring-black/5 focus:outline-none text-sm z-dropdown min-w-[180px]"
                            tabIndex={-1}
                            {...getFloatingProps({ onKeyDown: handleFloatingKeyDown })}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                            <MultiselectFilter value={query} onChange={handleFilter} isBusy={isFetching} />

                            <div className="p-1.5">
                                {optionsFiltered.map((option, idx) => (
                                    <div
                                        key={option.id}
                                        className="mb-1 last:mb-0"
                                        ref={(node) => {
                                            listRef.current[idx] = node;
                                        }}
                                        tabIndex={activeIndex === idx ? 0 : -1}
                                        {...getItemProps({
                                            onClick() {
                                                handleToggle(option.id);
                                            },
                                            onKeyDown(e) {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleToggle(option.id);
                                                }
                                            },
                                        })}
                                    >
                                        <MultiselectItem
                                            checked={isChecked(option.id)}
                                            onChange={() => handleToggle(option.id)}
                                            active={activeIndex === idx}
                                        >
                                            {option.label}
                                        </MultiselectItem>
                                    </div>
                                ))}
                                {hasNextPage && (
                                    <div
                                        className="text-center text-gray-500 text-xs flex justify-center py-2"
                                        ref={sentinelRef}
                                    >
                                        {isFetching ? (
                                            <Spinner />
                                        ) : (
                                            <IconButton
                                                variant="ghost"
                                                icon={<OutlineArrowBottom />}
                                                onClick={fetchNextPage}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                            {emptyOption && (
                                <div className="border-t bg-white border-gray-200 p-1.5 sticky bottom-0">
                                    <MultiselectItem
                                        checked={isChecked(emptyOption.id)}
                                        onChange={() => handleToggle(emptyOption.id)}
                                    >
                                        {emptyOption.label}
                                    </MultiselectItem>
                                </div>
                            )}
                        </motion.div>
                    </FloatingFocusManager>
                )}
            </AnimatePresence>
        </div>
    );
};

MultiselectInfinite.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
        }),
    ).isRequired,
    optionsSelected: PropTypes.array,
    onChangeSelected: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    query: PropTypes.string.isRequired,
    onChangeQuery: PropTypes.func.isRequired,
    isFetching: PropTypes.bool,
    fetchNextPage: PropTypes.func,
    hasNextPage: PropTypes.bool,
    emptyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    floatLabel: PropTypes.string,
    enableClear: PropTypes.bool,
    onClear: PropTypes.func,
    size: PropTypes.oneOf(['normal', 'small']),
};

export default MultiselectInfinite;
